import { floodFill } from "./fill";
export const SIZE = 512;
export const PAPER = "#ffffff";
export type Tool = "pen" | "eraser" | "fill" | "line" | "rectangle" | "ellipse" | "picker" | "hand";
export interface Point {
  x: number;
  y: number;
}
export interface Mark {
  tool: "pen" | "eraser" | "fill" | "line" | "rectangle" | "ellipse" | "clear";
  color: string;
  size: number;
  points: Point[];
}
export function paint(g: CanvasRenderingContext2D, marks: Mark[], reset = true) {
  if (reset) {
    g.fillStyle = PAPER;
    g.fillRect(0, 0, SIZE, SIZE);
  }
  for (const m of marks) {
    if (m.tool === "clear") {
      g.fillStyle = PAPER;
      g.fillRect(0, 0, SIZE, SIZE);
      continue;
    }
    const first = m.points[0];
    const last = m.points.at(-1);
    if (!first || !last) continue;
    if (m.tool === "fill") {
      const pixels = g.getImageData(0, 0, SIZE, SIZE);
      floodFill(pixels.data, SIZE, SIZE, first.x, first.y, m.color);
      g.putImageData(pixels, 0, 0);
      continue;
    }
    g.strokeStyle = g.fillStyle = m.tool === "eraser" ? PAPER : m.color;
    g.lineWidth = m.size;
    g.lineCap = g.lineJoin = "round";
    g.beginPath();
    if (m.points.length === 1 || (first.x === last.x && first.y === last.y && m.points.length === 2)) {
      g.arc(first.x, first.y, m.size / 2, 0, Math.PI * 2);
      g.fill();
    } else if (m.tool === "rectangle") {
      g.strokeRect(first.x, first.y, last.x - first.x, last.y - first.y);
    } else if (m.tool === "ellipse") {
      g.ellipse(
        (first.x + last.x) / 2,
        (first.y + last.y) / 2,
        Math.abs(last.x - first.x) / 2,
        Math.abs(last.y - first.y) / 2,
        0,
        0,
        Math.PI * 2,
      );
      g.stroke();
    } else {
      g.moveTo(first.x, first.y);
      if (m.tool === "line") g.lineTo(last.x, last.y);
      else {
        for (let i = 1; i < m.points.length - 1; i++) {
          const p = m.points[i]!;
          const next = m.points[i + 1]!;
          g.quadraticCurveTo(p.x, p.y, (p.x + next.x) / 2, (p.y + next.y) / 2);
        }
        g.lineTo(last.x, last.y);
      }
      g.stroke();
    }
  }
}
export function hasInk(marks: Mark[]): boolean {
  const lastClear = marks.findLastIndex((m) => m.tool === "clear");
  return marks.slice(lastClear + 1).some((m) => m.tool !== "eraser" && m.color.toLowerCase() !== PAPER);
}
export function readDraft(raw: string | null): Mark[] {
  if (!raw || raw.length > 2_000_000) return [];
  try {
    const value: unknown = JSON.parse(raw);
    if (!Array.isArray(value) || value.length > 5000) return [];
    const valid = value.every((m: unknown) => {
      if (!m || typeof m !== "object") return false;
      const mark = m as Partial<Mark>;
      return (
        ["pen", "eraser", "fill", "line", "rectangle", "ellipse", "clear"].includes(mark.tool ?? "") &&
        typeof mark.color === "string" &&
        /^#[\da-f]{6}$/i.test(mark.color) &&
        typeof mark.size === "number" &&
        mark.size >= 1 &&
        mark.size <= 96 &&
        Array.isArray(mark.points) &&
        mark.points.length <= 50_000 &&
        mark.points.every((p: unknown) => {
          if (!p || typeof p !== "object") return false;
          const point = p as Partial<Point>;
          return (
            typeof point.x === "number" &&
            Number.isFinite(point.x) &&
            point.x >= 0 &&
            point.x <= SIZE &&
            typeof point.y === "number" &&
            Number.isFinite(point.y) &&
            point.y >= 0 &&
            point.y <= SIZE
          );
        })
      );
    });
    return valid ? (value as Mark[]) : [];
  } catch {
    return [];
  }
}
